import Axios from 'axios';
import { environment } from '@/environments/environment';

const STORAGE_KEY = 'qtrace-auth';
const LEGACY_STORAGE_KEY = 'qtrace-access';

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

export interface AuthSessionData {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  user_id?: string;
  name?: string;
  email?: string;
}

interface StoredAuthSession extends AuthSessionData {
  expires_at: number;
}

interface RefreshApiResponse {
  status: string;
  message: string;
  data: AuthSessionData;
}

const refreshClient = Axios.create({
  baseURL: `${environment.apiUrl}`,
});

let refreshPromise: Promise<boolean> | null = null;

function readRawSession(): StoredAuthSession | null {
  try {
    const raw =
      sessionStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const session = JSON.parse(raw) as StoredAuthSession;
    if (!session.access_token) {
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

function isRememberedSession(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

export function isAuthenticated(): boolean {
  const session = readRawSession();
  if (!session) {
    return false;
  }

  if (session.expires_at > Date.now()) {
    return true;
  }

  return Boolean(session.refresh_token);
}

export function getAccessToken(): string | null {
  const session = readRawSession();
  if (!session || session.expires_at <= Date.now()) {
    return null;
  }

  return session.access_token;
}

export function saveAuthSession(
  data: AuthSessionData,
  remember = false,
): void {
  const expires_at = Date.now() + data.expires_in * 1000;

  const session: StoredAuthSession = {
    ...data,
    expires_at,
  };

  const serialized = JSON.stringify(session);

  sessionStorage.setItem(STORAGE_KEY, serialized);

  try {
    if (remember) {
      localStorage.setItem(STORAGE_KEY, serialized);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
}

export function clearAuthSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
}

export function notifyUnauthorized(): void {
  clearAuthSession();
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
}

export async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const current = readRawSession();
    if (!current?.refresh_token) {
      return false;
    }

    try {
      const response = await refreshClient.post<RefreshApiResponse>(
        '/api/auth/refresh',
        { refresh_token: current.refresh_token },
      );

      const data = response.data?.data;
      if (!data?.access_token) {
        return false;
      }

      saveAuthSession(
        {
          access_token: data.access_token,
          refresh_token: data.refresh_token ?? current.refresh_token,
          expires_in: data.expires_in,
          user_id: data.user_id ?? current.user_id,
          name: data.name ?? current.name,
          email: data.email ?? current.email,
        },
        isRememberedSession(),
      );

      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}
